/**
 * Enhanced Welcome Banner with contextual CTAs for new users
 * Provides guided experience based on user state
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Wallet, ShoppingBag, Sparkles } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useNewUserDetection } from '../../hooks/useNewUserDetection';


interface EnhancedWelcomeBannerProps {
  username: string;
  message?: string;
  operationalInfo?: string;
}

export const EnhancedWelcomeBanner: React.FC<EnhancedWelcomeBannerProps> = ({ 
  username, 
  message = "Kelola akun Business Manager dan Personal Facebook Anda dengan mudah.",
  operationalInfo 
}) => {
  const { isNewUser, needsOnboarding, daysSinceRegistration } = useNewUserDetection();

  const getWelcomeMessage = () => {
    if (isNewUser && daysSinceRegistration === 0) {
      return "Selamat bergabung! Mari mulai perjalanan digital Anda bersama kami.";
    }
    if (isNewUser) {
      return `Hari ke-${daysSinceRegistration + 1} bersama Canvango. ${message}`;
    }
    return message;
  };

  const getQuickActions = () => {
    if (!needsOnboarding) return null;

    return (
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Primary CTA - Top Up */}
        <Link
          to="/top-up"
          className="group bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-2xl p-4 border border-white/30 hover:border-white/50 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-green-500/20 p-2 rounded-xl">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-sm">Top Up Saldo</h3>
              <p className="text-blue-100 text-xs">Mulai dengan mengisi saldo</p>
            </div>
            <FontAwesomeIcon 
              icon={faArrowRight} 
              className="w-4 h-4 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" 
            />
          </div>
        </Link>

        {/* Secondary CTA - Browse Products */}
        <Link
          to="/akun-bm"
          className="group bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-2xl p-4 border border-white/25 hover:border-white/40 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/20 p-2 rounded-xl">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-sm">Lihat Produk</h3>
              <p className="text-blue-100 text-xs">Jelajahi akun tersedia</p>
            </div>
            <FontAwesomeIcon 
              icon={faArrowRight} 
              className="w-4 h-4 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" 
            />
          </div>
        </Link>
      </div>
    );
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-[#0866FF] via-[#0A7CFF] to-[#0D96FF] rounded-3xl p-6 sm:p-8 mb-6 shadow-2xl">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-400/30 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent"></div>
      
      {/* Animated gradient orbs */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-blue-300/25 via-blue-400/15 to-transparent rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-gradient-to-tr from-blue-500/20 via-blue-600/10 to-transparent rounded-full blur-3xl"></div>
      
      {/* New User Badge */}
      {isNewUser && (
        <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1 shadow-lg">
          <Sparkles className="w-3 h-3" />
          <span>Member Baru</span>
        </div>
      )}
      
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '24px 24px'
      }}></div>
      
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/5"></div>
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 backdrop-blur-md p-2.5 rounded-xl shadow-lg border border-white/30">
              <Home className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl text-white font-bold tracking-tight">
                Selamat Datang, {username}!
              </h1>
            </div>
          </div>
        </div>

        {/* Welcome Message */}
        <p className="text-blue-50 text-sm sm:text-base leading-relaxed font-medium mb-4">
          {getWelcomeMessage()}
        </p>

        {/* Operational Info */}
        {operationalInfo && (
          <div className="mb-4 bg-white/15 backdrop-blur-md rounded-2xl p-3 border border-white/25 shadow-lg">
            <p className="text-xs sm:text-sm leading-relaxed text-white/95">{operationalInfo}</p>
          </div>
        )}

        {/* Quick Actions for New Users */}
        {getQuickActions()}

        {/* Progress Indicator for New Users */}
        {needsOnboarding && (
          <div className="mt-4 bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
            <div className="flex items-center justify-between text-xs text-blue-100">
              <span>Langkah selanjutnya:</span>
              <span>1 dari 2</span>
            </div>
            <div className="mt-2 bg-white/20 rounded-full h-2">
              <div className="bg-gradient-to-r from-green-400 to-blue-400 h-2 rounded-full w-1/2 transition-all duration-500"></div>
            </div>
            <p className="text-xs text-blue-100 mt-2">
              Top up saldo untuk mulai berbelanja
            </p>
          </div>
        )}
      </div>
    </div>
  );
};